import { ArrowUpRight, Clock, BarChart3, Layers, Wrench } from "lucide-react";
import { Reveal, Eyebrow } from "@/lib/motion";
import { ACADEMY_CATEGORIES, COURSES, IMG, LINKS } from "@/data/site";

/* ---------------- AI Academy ---------------- */
export const Academy = () => (
  <section id="academy" className="relative py-24 md:py-32 scroll-mt-20" data-testid="academy-section">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <Reveal><Eyebrow>AI Academy</Eyebrow></Reveal>
          <Reveal delay={0.1}>
            <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">Welcome to SuhaniEdge AI Academy</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="mt-5 text-lg text-slate-400 max-w-lg">Learn the skills behind the tools. Build with AI — across a growing ecosystem of practical categories.</p>
          </Reveal>
        </div>
        <Reveal delay={0.15}>
          <div className="relative rounded-3xl overflow-hidden border border-white/10">
            <img src={IMG.students} alt="SuhaniEdge AI Academy students in a live session" className="w-full h-72 object-cover object-top" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/80 to-transparent" />
            <span className="absolute bottom-4 left-5 text-sm text-white/90 font-medium">Live at SuhaniEdge AI Academy</span>
          </div>
        </Reveal>
      </div>

      <div className="mt-14 grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
        {ACADEMY_CATEGORIES.map((c, i) => (
          <Reveal key={c} delay={i * 0.04}>
            <div data-testid={`academy-cat-${i}`} className="group h-full rounded-2xl border border-white/5 bg-ink-surface px-5 py-6 transition-all duration-300 hover:border-brand/40 hover:bg-brand/5 cursor-default">
              <span className="text-xs text-slate-500 font-semibold">{String(i + 1).padStart(2, "0")}</span>
              <p className="mt-2 font-heading text-base font-medium text-white group-hover:text-brand-light transition-colors">{c}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

/* ---------------- Featured Course ---------------- */
export const FeaturedCourse = () => (
  <section className="relative py-16" data-testid="featured-course">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <Reveal>
        <div className="grid lg:grid-cols-2 rounded-[2rem] overflow-hidden border border-white/10 bg-ink-surface">
          <div className="relative min-h-[320px]">
            <img src={IMG.desk} alt="Featured Generative AI course" className="absolute inset-0 h-full w-full object-cover" loading="lazy" />
            <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink-surface/90 lg:to-ink-surface" />
            <span className="absolute top-6 left-6 rounded-full bg-brand text-white text-xs font-semibold px-4 py-1.5 uppercase tracking-wider">Featured</span>
          </div>
          <div className="p-8 md:p-12">
            <p className="text-xs uppercase tracking-[0.25em] text-brand-light font-semibold">Generative AI</p>
            <h3 className="mt-4 font-heading text-3xl md:text-4xl font-semibold text-white">[Featured Course Name]</h3>
            <p className="mt-4 text-slate-400">Learn practical Generative AI from the ground up.</p>
            <div className="mt-8 grid grid-cols-2 gap-5">
              {[{ i: BarChart3, l: "Level", v: "Beginner → Intermediate" }, { i: Clock, l: "Duration", v: "Duration TBD" }, { i: Layers, l: "Format", v: "Self-paced + live" }, { i: Wrench, l: "Projects", v: "Hands-on projects" }].map((x) => (
                <div key={x.l} className="flex items-start gap-3">
                  <x.i className="h-5 w-5 text-brand-light mt-0.5" />
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wider">{x.l}</p>
                    <p className="text-sm text-white font-medium">{x.v}</p>
                  </div>
                </div>
              ))}
            </div>
            <p className="mt-6 text-sm text-slate-500">Tools covered: ChatGPT, Midjourney, and more <span className="text-slate-600">(editable placeholder)</span></p>
            <a href={LINKS.connect} target="_blank" rel="noopener noreferrer" data-testid="featured-view-btn" className="mt-8 group inline-flex items-center gap-2 rounded-full bg-brand hover:bg-brand-light text-white font-semibold px-7 py-3.5 transition-colors">
              View Course <ArrowUpRight className="h-4 w-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </a>
          </div>
        </div>
      </Reveal>
    </div>
  </section>
);

/* ---------------- Course Grid ---------------- */
export const CourseGrid = () => (
  <section id="courses" className="relative py-24 md:py-32 scroll-mt-20" data-testid="course-grid">
    <div className="mx-auto max-w-7xl px-6 lg:px-10">
      <div className="max-w-2xl">
        <Reveal><Eyebrow>Courses</Eyebrow></Reveal>
        <Reveal delay={0.1}>
          <h2 className="mt-5 font-heading text-4xl md:text-5xl font-semibold text-white tracking-tight">A growing catalogue of practical AI courses</h2>
        </Reveal>
      </div>
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {COURSES.map((c, i) => (
          <Reveal key={i} delay={(i % 3) * 0.08}>
            <article data-testid={`course-card-${i}`} className="group h-full flex flex-col rounded-3xl overflow-hidden border border-white/5 bg-ink-surface transition-all duration-300 hover:-translate-y-1.5 hover:border-brand/40">
              <div className="relative h-44 overflow-hidden">
                <img src={c.img} alt={`${c.category} course`} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-ink-surface via-transparent to-transparent" />
                {c.soon && <span className="absolute top-4 right-4 rounded-full bg-white/10 backdrop-blur text-white text-xs font-semibold px-3 py-1">Coming Soon</span>}
                {c.live && <span className="absolute top-4 left-4 inline-flex items-center gap-1.5 rounded-full bg-red-500/90 backdrop-blur text-white text-xs font-semibold px-3 py-1"><span className="h-1.5 w-1.5 rounded-full bg-white animate-pulse" />LIVE</span>}
              </div>
              <div className="flex flex-col flex-1 p-6">
                <p className="text-xs uppercase tracking-[0.2em] text-brand-light font-semibold">{c.category}</p>
                <h3 className="mt-3 font-heading text-xl font-semibold text-white">{c.title}</h3>
                <p className="mt-2 text-sm text-slate-400 flex-1">{c.desc}</p>
                <div className="mt-5 flex items-center justify-between border-t border-white/5 pt-4">
                  <span className="text-xs text-slate-400 font-medium">{c.level}</span>
                  <a href={LINKS.connect} target="_blank" rel="noopener noreferrer" data-testid={`course-view-${i}`} className="inline-flex items-center gap-1 text-sm font-semibold text-brand-light hover:text-white transition-colors">
                    View Course <ArrowUpRight className="h-3.5 w-3.5" />
                  </a>
                </div>
              </div>
            </article>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);
